import { useCallback, useMemo, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';

const DIRECT_STROKE = '#3b82f6';
const PATH_STROKE = '#000000';

export interface EdgeHoverHighlight<E extends Edge> {
  edges: E[];
  hoveredNodeId: string | null;
  onNodeMouseEnter: (event: unknown, node: Node) => void;
  onNodeMouseLeave: () => void;
}

/**
 * When a node is hovered, paint the edges directly touching it solid blue and
 * the rest of the upstream/downstream path solid black so users can trace how
 * a step fits into the broader graph.
 */
export function useEdgeHoverHighlight<E extends Edge>(
  edges: E[],
): EdgeHoverHighlight<E> {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const onNodeMouseEnter = useCallback((_: unknown, node: Node) => {
    setHoveredNodeId(node.id);
  }, []);
  const onNodeMouseLeave = useCallback(() => setHoveredNodeId(null), []);

  const decoratedEdges = useMemo(() => {
    if (!hoveredNodeId) return edges;
    const incomingByTarget = new Map<string, E[]>();
    const outgoingBySource = new Map<string, E[]>();
    for (const edge of edges) {
      const inList = incomingByTarget.get(edge.target);
      if (inList) inList.push(edge);
      else incomingByTarget.set(edge.target, [edge]);
      const outList = outgoingBySource.get(edge.source);
      if (outList) outList.push(edge);
      else outgoingBySource.set(edge.source, [edge]);
    }
    const direct = new Set<string>();
    const path = new Set<string>();

    const upstreamQueue: string[] = [hoveredNodeId];
    const visitedUp = new Set<string>([hoveredNodeId]);
    while (upstreamQueue.length > 0) {
      // oxlint-disable-next-line no-non-null-assertion -- length>0 guarantees shift returns
      const node = upstreamQueue.shift()!;
      for (const edge of incomingByTarget.get(node) ?? []) {
        if (node === hoveredNodeId) direct.add(edge.id);
        else path.add(edge.id);
        if (!visitedUp.has(edge.source)) {
          visitedUp.add(edge.source);
          upstreamQueue.push(edge.source);
        }
      }
    }
    const downstreamQueue: string[] = [hoveredNodeId];
    const visitedDown = new Set<string>([hoveredNodeId]);
    while (downstreamQueue.length > 0) {
      // oxlint-disable-next-line no-non-null-assertion -- length>0 guarantees shift returns
      const node = downstreamQueue.shift()!;
      for (const edge of outgoingBySource.get(node) ?? []) {
        if (node === hoveredNodeId) direct.add(edge.id);
        else path.add(edge.id);
        if (!visitedDown.has(edge.target)) {
          visitedDown.add(edge.target);
          downstreamQueue.push(edge.target);
        }
      }
    }
    if (direct.size === 0 && path.size === 0) return edges;
    return edges.map((edge) => {
      if (direct.has(edge.id)) {
        return {
          ...edge,
          animated: false,
          style: {
            ...(edge.style ?? {}),
            stroke: DIRECT_STROKE,
            strokeWidth: 3,
          },
        };
      }
      if (path.has(edge.id)) {
        return {
          ...edge,
          animated: false,
          style: {
            ...(edge.style ?? {}),
            stroke: PATH_STROKE,
            strokeWidth: 2,
          },
        };
      }
      return edge;
    });
  }, [edges, hoveredNodeId]);

  return {
    edges: decoratedEdges,
    hoveredNodeId,
    onNodeMouseEnter,
    onNodeMouseLeave,
  };
}
